import MessageHelper from './message-helper';

interface CheckboxOptions {
  name: string;
  isChecked?: boolean;
  inputClass?: string;
  dataAttributes?: Record<string, string>;
}

enum ButtonIconPosition { Left = 'LEFT', Right = 'RIGHT' }

interface ButtonOptions {
  text: string;
  buttonClass?: string;
  textClass?: string;
  icon?: string;
  iconPosition?: ButtonIconPosition;
  dataAttributes?: Record<string, string>;
}

interface LinkButtonOptions extends ButtonOptions {
  href: string;
}

interface InputOptions {
  name: string;
  label?: string;
  labelClassList?: string;
  classList?: string;
  placeholder?: string;
  type?: 'text' | 'number'
  required?: boolean;
  disabled?: boolean;
}

interface TableCellOptions {
  children?: HTMLElement | HTMLElement[],
  colSpan?: number
}

export default class HTMLTemplateHelper {
  static createButton(options: ButtonOptions): HTMLButtonElement {
    const button = document.createElement('button');
    this.populateButton(button, options);
    return button;
  }

  static createLinkButton(options: LinkButtonOptions): HTMLAnchorElement {
    const link = document.createElement('a');
    link.href = options.href;

    this.populateButton(link, options);
    return link;
  }

  static createCheckbox(options: CheckboxOptions): HTMLLabelElement {
    const label = document.createElement('label');
    label.classList.add('checkbox-label', 'input-label');

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = options.name;
    if (options.isChecked) {
      input.checked = true;
      input.setAttribute('checked', 'checked');
    }
    input.classList.add('checkbox__input');
    this.splitAddClassList(input, options.inputClass);
    this.addDataAttributes(input, options.dataAttributes);

    const icon = document.createElement('i');
    icon.classList.add('checkbox__icon', 'fas', 'fa-check');

    label.append(input);
    label.append(icon);

    return label;
  }

  static createTableRow(): HTMLTableRowElement {
    const tr = document.createElement('tr');
    tr.classList.add('table__row');
    return tr;
  }

  static createTableCell(options: TableCellOptions = {}): HTMLTableCellElement {
    const { children, colSpan } = options;

    const td = document.createElement('td');
    td.classList.add('table__column');
    if (colSpan) {
      td.colSpan = colSpan;
    }

    const contentSpan = document.createElement('span');
    contentSpan.classList.add('column__content');
    td.append(contentSpan);

    if (Array.isArray(children)) {
      children.forEach((child) => contentSpan.append(child));
    } else if (children) {
      contentSpan.append(children);
    }

    return td;
  }

  static createInput(options: InputOptions): HTMLDivElement {
    const container = document.createElement('div');
    container.classList.add('form-group');

    if (options.label) {
      const label = document.createElement('label');
      label.classList.add('form-label');
      label.textContent = options.label;
      this.splitAddClassList(label, options.labelClassList);
      container.append(label);
    }

    const input = document.createElement('input');
    input.classList.add('form-control');
    input.name = options.name;
    input.type = options.type ?? 'text';
    input.disabled = options.disabled ?? false;
    input.required = options.required ?? false;
    this.splitAddClassList(input, options.classList);
    container.append(input);

    return container;
  }

  private static splitAddClassList(element: HTMLElement, classList?: string): void {
    classList
      ?.split(' ')
      .forEach((className) => {
        element.classList.add(className);
      });
  }

  private static addDataAttributes(element: HTMLElement, attributes?: Record<string, string>): void {
    if (attributes) {
      Object.keys(attributes)
        .forEach((key) => {
          // eslint-disable-next-line no-param-reassign
          element.dataset[key] = attributes[key];
        });
    }
  }

  private static populateButton(element: HTMLElement, options: ButtonOptions): void {
    element.classList.add('button');

    this.splitAddClassList(element, options.buttonClass);

    let icon: HTMLElement | null = null;
    if (options.icon) {
      icon = document.createElement('i');
      icon.classList.add('button__icon');
      this.splitAddClassList(icon, options.icon);
      if (options.iconPosition !== ButtonIconPosition.Right) {
        element.append(icon);
      }
    }

    const text = document.createElement('span');
    text.textContent = options.text;
    text.classList.add('button__text');
    this.splitAddClassList(text, options.textClass);
    element.append(text);

    if (icon && options.iconPosition === ButtonIconPosition.Right) {
      element.append(icon);
    }

    this.addDataAttributes(element, options.dataAttributes);
  }

  static createFromString(html: string): HTMLElement {
    const template = document.createElement('template');
    html = html.trim(); // eslint-disable-line no-param-reassign
    template.innerHTML = html;
    return template.content.firstChild as HTMLElement;
  }

  static createStatusLabel(options: { statusCode: string, classList?: string }): HTMLElement {
    let statusClass = '';
    // eslint-disable-next-line default-case
    switch (options.statusCode) {
      case 'OPEN':
      case 'IN_CART':
        statusClass = '-blue';
        break;
      case 'ORDERED':
      case 'ORDERED_MANUAL':
        statusClass = '-green';
        break;
      case 'ARCHIVED':
        statusClass = '-grey';
        break;
      case 'DELIVERED':
      case 'DELIVERED_MANUAL':
        statusClass = '-black';
        break;
      case 'EXPIRED':
        statusClass = '-red';
        break;
    }

    const statusMessageCode = `eparts.status.${options.statusCode}.name`;

    return this.createFromString(`
      <p class="status-label ${statusClass} ${options.classList || ''}">
        <span>
          ${MessageHelper.getMessage(statusMessageCode)}
       </span>
      </p>
    `);
  }
}
